import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import BatchActionDialog from '../BatchActionDialog';
import BatchFailedDialog from '../BatchFailedDialog';
import ProgressDialog from '../ProgressDialog';
import scheduleTask from '@afosto/utils-react/tasks/scheduleTask';
import { actions as actionsApi } from '@afosto/content/api/Actions';
import { batches as batchesApi } from '@afosto/content/api/Batches';
import { dataset as datasetApi } from '@afosto/content/api/Dataset';

const BatchDialog = ({
  actionConfig,
  actionId,
  batchCreateLabel,
  batchFailedLabel,
  batchStartLabel,
  batchValidationLabel,
  isOpen,
  onClose,
  onFailed,
  onFinish,
  reference,
  title,
}) => {
  const [action, setAction] = useState(null);
  const [isCreatingBatch, setIsCreatingBatch] = useState(false);
  const [isValidatingBatch, setIsValidatingBatch] = useState(false);
  const [isConfirmingBatch, setIsConfirmingBatch] = useState(false);
  const [showBatchActionDialog, setShowBatchActionDialog] = useState(false);
  const [showBatchFailedDialog, setShowBatchFailedDialog] = useState(false);

  useEffect(() => {
    let isMounted = true;

    if (isMounted) {
      setShowBatchActionDialog(isOpen);
    }

    return () => {
      isMounted = false;
    };
  }, [isOpen]);

  useEffect(() => {
    let isMounted = true;

    const fetchAction = async () => {
      try {
        const response = await actionsApi.getAction(actionId);
        const { data = null } = response;

        if (isMounted) {
          setAction(data);
        }
      } catch (err) {
        if (isMounted) {
          setAction(null);
        }
      }
    };

    if (actionId) {
      fetchAction();
    }

    return () => {
      isMounted = false;
    };
  }, [actionId]);

  const handleConfirmBatch = async batch => {
    setIsConfirmingBatch(true);

    try {
      await batchesApi.confirmBatch(batch?.id);
      return true;
    } catch (err) {
      return Promise.reject(err);
    } finally {
      setIsConfirmingBatch(false);
    }
  };

  const handleValidateBatch = async batch => {
    try {
      setIsValidatingBatch(true);

      const validationResponse = await batchesApi.validateBatch(batch?.id);
      const taskId = validationResponse.headers?.['x-task-id'];

      await scheduleTask(taskId);

      return true;
    } catch (err) {
      return Promise.reject(err);
    } finally {
      setIsValidatingBatch(false);
    }
  };

  const handleCreatBatch = async ({ action: batchAction, batchReference, config: batchConfig }) => {
    try {
      setIsCreatingBatch(true);

      const datasetResponse = await datasetApi.createDataset({
        reference: batchReference,
      });
      const { data: dataset } = datasetResponse;

      const batchResponse = await batchesApi.createBatch({
        action_id: batchAction?.id,
        reference: batchReference,
        dataset_id: dataset?.id,
        config: batchConfig,
      });
      const { data: batch } = batchResponse;

      return batch;
    } catch (err) {
      return Promise.reject(err);
    } finally {
      setIsCreatingBatch(false);
    }
  };

  const handleStartBatch = async batchData => {
    try {
      setShowBatchActionDialog(false);

      const batch = await handleCreatBatch(batchData);

      await handleValidateBatch(batch);
      await handleConfirmBatch(batch);

      if (onFinish && typeof onFinish === 'function') {
        onFinish(batch);
      }
    } catch (err) {
      setShowBatchFailedDialog(true);
    }
  };

  const handleBatchFailed = () => {
    setShowBatchFailedDialog(false);

    if (onFailed && typeof onFailed === 'function') {
      onFailed();
    }
  };

  return (
    <>
      <BatchActionDialog
        action={action}
        actionConfig={actionConfig}
        isOpen={showBatchActionDialog}
        onClose={onClose}
        onSubmit={handleStartBatch}
        reference={reference}
        title={title}
      />
      <ProgressDialog isOpen={isCreatingBatch} message={batchCreateLabel} />
      <ProgressDialog isOpen={isValidatingBatch} message={batchValidationLabel} />
      <ProgressDialog isOpen={isConfirmingBatch} message={batchStartLabel} />
      <BatchFailedDialog
        isOpen={showBatchFailedDialog}
        message={batchFailedLabel}
        onClose={handleBatchFailed}
      />
    </>
  );
};

BatchDialog.propTypes = {
  actionConfig: PropTypes.object,
  actionId: PropTypes.string,
  batchCreateLabel: PropTypes.string,
  batchFailedLabel: PropTypes.string,
  batchStartLabel: PropTypes.string,
  batchValidationLabel: PropTypes.string,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  onFailed: PropTypes.func,
  onFinish: PropTypes.func,
  reference: PropTypes.string,
  title: PropTypes.string.isRequired,
};

BatchDialog.defaultProps = {
  actionConfig: {},
  actionId: undefined,
  isOpen: false,
  onClose: undefined,
  onFailed: undefined,
  onFinish: undefined,
  reference: undefined,
  batchCreateLabel: 'Creating batch',
  batchFailedLabel: 'Batch failed',
  batchStartLabel: 'Batch starting',
  batchValidationLabel: 'Validating batch',
};

export default BatchDialog;
